import { Badge } from '@heidi/design-system/badge';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerTitle,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import {
  SegmentedControl,
  SegmentedControlContent,
  SegmentedControlList,
  SegmentedControlTrigger,
} from '@heidi/design-system/segmented-control';
import { TypographyP, typographyVariant } from '@heidi/design-system/typography';
import { ModalId } from '@heidi/schema/ml-scribe';
import { cn } from '@heidi/utils';
import { FormattedMessage } from 'react-intl';

import { getCommerceBillingUiPorts } from '../../settings/billing/billing-ui-ports';
import { getCommerceTrialsUiPorts } from '../trials-ui-ports';
import { ClinicianTrialEndedFeatureList } from './clinician-trial-ended-feature-list';
import { ClinicianTrialEndedModalFooter } from './clinician-trial-ended-modal-footer';
import { clinicianTrialEndedModalMessages } from './clinician-trial-ended-modal-messages';
import { ScribePlusRetainedFeatureList } from './scribe-plus-retained-feature-list';
import { useClinicianTrialEndedModal } from './useClinicianTrialEndedModal';

const SEGMENT_CLINICIAN = 'clinician';
const SEGMENT_SCRIBE_PLUS = 'scribe_plus';

/**
 * Post–Clinician/Practice trial-end modal (modal-queue launch via
 * {@link ModalId.NINETY_DAY_TRIAL_END_MODAL}). After a 90-day trial ends,
 * presents the user with a side-by-side recap of their previous trial tier
 * vs the paid tier they returned to (Clinician ↔ Scribe Plus, or
 * Practice ↔ Scribe Team), with cohort-specific CTAs:
 *
 * - **Solo Clinician / Practice admin (Stripe)**: "Upgrade to ..." (primary)
 *   + "Remain on Scribe Plus / Team" (secondary) + Terms/Privacy disclaimer.
 *   Practice admins also see a sales contact sub-line.
 * - **Practice admin (non-Stripe)**: single "Talk to us" sales CTA.
 * - **Practice non-admin**: single "Request to upgrade" CTA that opens the
 *   {@link TeamTrialUpgradeRequestModal} so they can ask their admin to
 *   upgrade.
 *
 * Low-usage admins reach this modal via the
 * {@link TrialEndDiscreetCard}'s "Learn more" CTA (user-triggered enqueue),
 * not as a launch modal — see `launchModalsConfigAtom` for the matrix.
 *
 * Figma:
 * - Solo Clinician (Clinician tab): https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=10978-30228
 * - Solo Clinician (Scribe Plus tab): https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=10978-31841
 * - Practice admin: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=10978-31030
 * - Non-admin: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=10993-35498
 */
export function ClinicianTrialEndedModal() {
  const { isOpen, handleClose } = getCommerceTrialsUiPorts().useQueuedModal(
    ModalId.NINETY_DAY_TRIAL_END_MODAL
  );
  const {
    upgradePrimaryRef,
    termsOfUseUrl,
    privacyPolicyUrl,
    wasClinicianPlan,
    isNonAdmin,
    showContactSalesCTA,
    hasCallbackRequested,
    isUpgradePending,
    TierIcon,
    clinicianPriceDisplay,
    handleOpenChange,
    handleUpgradeClinician,
    handleUpgradePractice,
    handleRequestToUpgrade,
    handleRemain,
    handlePracticeTalkToUs,
    handleContactSalesClick,
  } = useClinicianTrialEndedModal(isOpen, handleClose);
  const { isInSessionEvidenceShown } = getCommerceTrialsUiPorts().useEvidenceAccess();

  if (getCommerceBillingUiPorts().isAppleCompliantBuild()) {
    return null;
  }

  const titleMessage = isNonAdmin
    ? clinicianTrialEndedModalMessages.titleNonAdmin
    : wasClinicianPlan
      ? clinicianTrialEndedModalMessages.title
      : clinicianTrialEndedModalMessages.titlePractice;
  const showSoloClinicianPrice =
    !isNonAdmin && wasClinicianPlan && !!clinicianPriceDisplay;
  const bodyMessage = isNonAdmin
    ? isInSessionEvidenceShown
      ? clinicianTrialEndedModalMessages.bodyNonAdmin
      : clinicianTrialEndedModalMessages.bodyNonAdminNoInSession
    : wasClinicianPlan
      ? showSoloClinicianPrice
        ? isInSessionEvidenceShown
          ? clinicianTrialEndedModalMessages.bodySoloClinicianWithPrice
          : clinicianTrialEndedModalMessages.bodySoloClinicianNoInSessionWithPrice
        : isInSessionEvidenceShown
          ? clinicianTrialEndedModalMessages.bodySoloClinician
          : clinicianTrialEndedModalMessages.bodySoloClinicianNoInSession
      : isInSessionEvidenceShown
        ? clinicianTrialEndedModalMessages.bodyPracticeAdmin
        : clinicianTrialEndedModalMessages.bodyPracticeAdminNoInSession;

  // Feature-list cohort drives which rows + section header copy renders.
  // Non-admin is its own branch (no "formerly" wording) — see component docs.
  const featureListCohort: 'clinician' | 'practice_admin' | 'non_admin' = isNonAdmin
    ? 'non_admin'
    : wasClinicianPlan
      ? 'clinician'
      : 'practice_admin';

  return (
    <DialogDrawer open={isOpen} onOpenChange={handleOpenChange}>
      <DialogDrawerContent
        data-testid="clinician-trial-ended-modal"
        className="gap-6 border-border bg-surface p-8 sm:max-w-[494px] sm:rounded-2xl"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          upgradePrimaryRef.current?.focus();
        }}
      >
        <div className="flex flex-col items-center gap-6">
          <div className="flex w-full flex-col gap-2">
            <DialogDrawerTitle
              className={cn(typographyVariant({ variant: 'h2' }), 'text-center')}
            >
              <FormattedMessage {...titleMessage} />
            </DialogDrawerTitle>
            <TypographyP className="text-center text-text-secondary">
              <FormattedMessage
                {...bodyMessage}
                values={
                  showSoloClinicianPrice ? { price: clinicianPriceDisplay } : undefined
                }
              />
            </TypographyP>
          </div>

          <div className="flex w-full flex-col gap-4 rounded-2xl border border-border p-4">
            <SegmentedControl defaultValue={SEGMENT_CLINICIAN} className="w-full gap-6">
              <SegmentedControlList className="w-full">
                <SegmentedControlTrigger
                  value={SEGMENT_CLINICIAN}
                  className="flex-1"
                  data-testid="clinician-trial-ended-tab-clinician"
                >
                  <FormattedMessage
                    {...(wasClinicianPlan
                      ? clinicianTrialEndedModalMessages.tabClinician
                      : clinicianTrialEndedModalMessages.tabPractice)}
                  />
                </SegmentedControlTrigger>
                <SegmentedControlTrigger
                  value={SEGMENT_SCRIBE_PLUS}
                  className="flex-1"
                  data-testid={
                    wasClinicianPlan
                      ? 'clinician-trial-ended-tab-scribe-plus'
                      : 'clinician-trial-ended-tab-scribe-team'
                  }
                >
                  <span className="inline-flex items-center gap-2">
                    <FormattedMessage
                      {...(wasClinicianPlan
                        ? clinicianTrialEndedModalMessages.tabScribePlus
                        : clinicianTrialEndedModalMessages.tabScribeTeam)}
                    />
                    {/* Blue "Current" pill indicates this is the plan the user
                        is on right now (post-trial they always land back on
                        Scribe Plus / Scribe Team). */}
                    <Badge
                      color="blue"
                      size="sm"
                      className="shrink-0 shadow-none"
                      data-testid="clinician-trial-ended-current-tier-badge"
                    >
                      <FormattedMessage
                        {...clinicianTrialEndedModalMessages.currentTierBadge}
                      />
                    </Badge>
                  </span>
                </SegmentedControlTrigger>
              </SegmentedControlList>

              <SegmentedControlContent
                value={SEGMENT_CLINICIAN}
                className="w-full border-0 p-0 shadow-none focus-visible:border-transparent"
              >
                <ClinicianTrialEndedFeatureList cohort={featureListCohort} />
              </SegmentedControlContent>

              <SegmentedControlContent
                value={SEGMENT_SCRIBE_PLUS}
                className="w-full border-0 p-0 shadow-none focus-visible:border-transparent"
              >
                <ScribePlusRetainedFeatureList
                  variant={wasClinicianPlan ? 'scribe_plus' : 'scribe_team'}
                />
              </SegmentedControlContent>
            </SegmentedControl>
          </div>

          <ClinicianTrialEndedModalFooter
            isClinicianPlan={wasClinicianPlan}
            isNonAdmin={isNonAdmin}
            showContactSalesCTA={showContactSalesCTA}
            hasCallbackRequested={hasCallbackRequested}
            isUpgradePending={isUpgradePending}
            termsOfUseUrl={termsOfUseUrl}
            privacyPolicyUrl={privacyPolicyUrl}
            upgradePrimaryRef={upgradePrimaryRef}
            TierIcon={TierIcon}
            onUpgradeClinician={handleUpgradeClinician}
            onUpgradePractice={handleUpgradePractice}
            onRequestToUpgrade={handleRequestToUpgrade}
            onRemain={handleRemain}
            onPracticeTalkToUs={handlePracticeTalkToUs}
            onContactSalesClick={handleContactSalesClick}
          />
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}
